import {
  ConsentGroupsDto,
  TermDto,
  TermGroupDto,
  TermIdentityDto,
  TermResponseDto,
} from '@act/shared/data-transfer-objects';
import {
  createAction,
  createSelector,
  createSlice,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit';
import { resetRedux } from '../actions/sharedActions';
import { selectMember } from 'redux/slices/selectedMemberSlice.slice';
import { selectSelectedMember } from 'redux/slices/memberSlice.slice';

import { consentRequests } from 'redux/requests/consents.requests';
import { useAppSelector } from 'redux/storeHooks';
import { ConsentState } from 'redux/types/consents.types';
import { createApiThunk } from '../utils/apiThunk';
import { addToEffectsMiddleware } from 'redux/utils/effectsMiddleware.util';

// THUNKS/ACTIONS ==============================================================
export const getConsentGroups = createApiThunk(
  consentRequests.getConsentGroups
);

export const getAllConsentTerms = createApiThunk(consentRequests.getAllTerms);

export const getConsentForm = createApiThunk(consentRequests.getConsentForm);

export const consentFormSuccess = createAction<ConsentGroupsDto>(
  'consent/consentFormSuccess'
);
export const consentIdentitySuccess = createAction<TermIdentityDto>(
  'consent/consentIdentityFormSuccess'
);
export const consentTermSuccess = createAction<TermResponseDto[]>(
  'consent/consentTermSuccess'
);

export const languageChanged = createAction<string>('consent/languageChanged');

// INITIAL STATE ===============================================================
export const initialState: ConsentState = {
  key: null,
  loadedLanguage: null,
  status: 'initial',
  termGroups: [],
  termGroupsUserId: null,
  hasLinkExpired: false,
  termIdentityId: null,
  allTerms: [],
  allTermsLoadingStatus: 'initial',
};

// SLICE =======================================================================
export const consentsSlice = createSlice({
  name: 'consents',
  initialState,
  reducers: {
    reset: () => {
      return { ...initialState };
    },
    setKey: (state, action: PayloadAction<string>) => {
      state.key = action.payload;
      return state;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getConsentForm.pending, (state, action) => {
        state.status = 'loading';
        state.key = action.meta.arg.params.key;
      })
      .addCase(getConsentForm.rejected, (state, action) => {
        state.status = 'failed';
        if (action.payload?.code === '410') {
          state.hasLinkExpired = true;
        }
      })
      .addCase(consentIdentitySuccess, (state, action) => {
        state.termIdentityId = action.payload.id;
      })
      .addCase(consentTermSuccess, (state, action) => {
        const newGroups = [...state.termGroups];
        action.payload.forEach(termResponse => {
          const group = newGroups.find(g =>
            g.terms.find(t => t.id === termResponse.termId)
          );
          if (group) {
            group.terms = group.terms.map(t => {
              if (t.id === termResponse.termId) {
                return { ...t, consented: termResponse.consented };
              }
              return t;
            });
          }
        });
        state.termGroups = newGroups;
      })
      .addCase(getConsentGroups.pending, (state, action) => {
        state.status = 'loading';
        const nextUserId = action.meta.arg.params.userId;
        // Clear stale groups when ownership changes so selectors cannot treat
        // the previous member's terms as belonging to the newly requested one.
        if (state.termGroupsUserId !== nextUserId) {
          state.termGroups = [];
        }
        state.termGroupsUserId = nextUserId;
      })
      .addCase(getConsentGroups.fulfilled, (state, action) => {
        // Ignore out-of-order responses after a faster member switch.
        if (action.meta.arg.params.userId !== state.termGroupsUserId) {
          return;
        }
        state.status = 'success';
        state.termGroups = action.payload;
      })
      .addCase(getConsentGroups.rejected, (state, action) => {
        if (action.meta.arg.params.userId !== state.termGroupsUserId) {
          return;
        }
        state.status = 'failed';
        // Drop ownership so member-scoped selectors return empty, but keep
        // last-known groups for unscoped UI (same as pre-race-fix behavior).
        state.termGroupsUserId = null;
      })
      .addCase(getAllConsentTerms.pending, state => {
        state.allTermsLoadingStatus = 'loading';
      })
      .addCase(
        getAllConsentTerms.fulfilled,
        (state, action: PayloadAction<TermDto[]>) => {
          state.allTerms = action.payload;
          state.allTermsLoadingStatus = 'success';
        }
      )
      .addCase(getAllConsentTerms.rejected, state => {
        state.allTermsLoadingStatus = 'failed';
      })
      .addMatcher(
        isAnyOf(consentFormSuccess, getConsentForm.fulfilled),
        (state, action) => {
          const newTermGroups = action.payload?.termGroups;
          if (!newTermGroups) {
            return;
          }

          state.status = 'success';
          state.termGroups = newTermGroups;
          state.termGroupsUserId = null;
        }
      );
  },
});

// REDUCER =====================================================================
export const consentReducer = consentsSlice.reducer;

// ACTIONS =====================================================================
export const consentActions = consentsSlice.actions;

// SELECTORS ===================================================================
interface ConsentsSliceSelectorProps {
  key?: string;
  [key: string]: unknown;
}

const selectConsentSliceState = state =>
  (state as { consent: typeof initialState })?.consent;

const selectTermGroupByKey: (
  state,
  params: ConsentsSliceSelectorProps
) => TermGroupDto = createSelector(
  [
    selectConsentSliceState,
    (_state, params: ConsentsSliceSelectorProps) => params.key,
  ],
  (consentState, key) => {
    return consentState.termGroups.find(tg => tg.key === key);
  }
);

const selectAllTerms: (state) => TermDto[] = createSelector(
  [selectConsentSliceState],
  consentState => {
    if (!consentState.termGroups) {
      return [];
    }
    return consentState.termGroups.reduce(
      (acc, tg) => [...acc, ...tg.terms],
      []
    );
  }
);

/**
 * Terms for a specific member when the consent slice was loaded for that member.
 * Returns empty while a different member's fetch is in flight (stale groups are
 * cleared on pending) so consumers never attribute another member's terms here.
 *
 * @param state - Root Redux state
 * @param params - Member id to match against `termGroupsUserId`
 * @returns Terms for that member, or empty when the slice belongs to someone else
 */
export const selectTermsForMember: (
  state,
  params: { memberId: string }
) => TermDto[] = createSelector(
  [
    selectConsentSliceState,
    selectAllTerms,
    (_state, params: { memberId: string }) => params.memberId,
  ],
  (consentState, terms, memberId) => {
    if (!memberId || consentState.termGroupsUserId !== memberId) {
      return [];
    }
    return terms;
  }
);

/**
 * Terms that are still on the client's current groups (signable / badge).
 *
 * @param term - Consent term DTO
 * @returns False when the term exists only for guide history
 */
export function isCurrentGroupConsentTerm(term: TermDto): boolean {
  return term.historyOnly !== true;
}

const selectFirstTermForForm: (state) => TermDto = createSelector(
  [selectConsentSliceState],
  consentState => {
    const allTerms: TermDto[] = consentState.termGroups.reduce(
      (acc, tg) => [...acc, ...tg.terms],
      []
    );
    return allTerms.find(
      t => t.consented === null && isCurrentGroupConsentTerm(t)
    );
  }
);

/**
 * Live status for a consent term key (TCPA / treat gates, signing form).
 * Prefers a current-group term. If none exists, falls back to a history-only
 * term so an unlinked Reject still blocks messaging and scheduling until a
 * same-key current term is accepted.
 *
 * @param state - Root Redux state
 * @param params - Term key to look up
 * @returns Current-group term, else history-only last signature, else undefined
 */
export const selectTermByKey: (
  state,
  params: ConsentsSliceSelectorProps
) => TermDto = createSelector(
  [
    selectAllTerms,
    (_state, params: ConsentsSliceSelectorProps) => params.key,
  ],
  (terms, key) => {
    return (
      terms.find(t => t.key === key && isCurrentGroupConsentTerm(t)) ??
      terms.find(t => t.key === key)
    );
  }
);

const selectTermIdentityId = state =>
  selectConsentSliceState(state).termIdentityId;

/**
 * Count of current-group terms that have not been accepted or rejected.
 *
 * @param state - Root Redux state
 * @returns Incomplete current-group term count
 */
export const selectIncompleteTermsCount = state =>
  selectAllTerms(state).reduce((acc, term) => {
    if (term.consented === null && isCurrentGroupConsentTerm(term)) {
      return acc + 1;
    }
    return acc;
  }, 0);

export const selectAllConsentTerms = createSelector(
  selectConsentSliceState,
  state => state.allTerms
);

// EXPORT HELPER ===============================================================
export const consentAppSelectors = {
  useSelectConsentState: () => useAppSelector(selectConsentSliceState),
  useSelectTermGroupByKey: (key: string) =>
    useAppSelector(state => selectTermGroupByKey(state, { key })),
  useSelectAllTerms: () => useAppSelector(selectAllTerms),
  useSelectTermsForMember: (memberId: string) =>
    useAppSelector(state => selectTermsForMember(state, { memberId })),
  useSelectTermByKey: (key: string) =>
    useAppSelector(state => selectTermByKey(state, { key })),
  useSelectFirstTermForForm: () => useAppSelector(selectFirstTermForForm),
  useSelectTermIdentityId: () => useAppSelector(selectTermIdentityId),
  useSelectIncompleteTermsCount: () =>
    useAppSelector(selectIncompleteTermsCount),
  useSelectAllConsentTerms: () => useAppSelector(selectAllConsentTerms),
};

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: selectMember,
    effect: async ({ dispatch, action }) => {
      if (action.payload) {
        dispatch(getConsentGroups({ params: { userId: action.payload } }));
      }
    },
  });

  addEffect({
    action: languageChanged,
    effect: async ({ dispatch, state }) => {
      const key = state.consent.key;

      if (key) {
        dispatch(getConsentForm({ params: { key } }));
      } else {
        const selectedMember = selectSelectedMember(state);

        if (selectedMember) {
          dispatch(getConsentGroups({ params: { userId: selectedMember.id } }));
        }
      }
    },
  });
});

// WEBSOCKET ACTIONS ===========================================================
